import { useEffect, useState, useSyncExternalStore } from "react";
import { createPortal } from "react-dom";
import { AlertIcon, CheckIcon, CloseIcon } from "@/components/ui/icons";
import { cn } from "@/lib/cn";
import { dismiss, getSnapshot, subscribe, type ToastRecord, type ToastTone } from "@/lib/toast";

const TONE: Record<
  ToastTone,
  { icon: typeof AlertIcon; rail: string; glyph: string; label: string }
> = {
  error: {
    icon: AlertIcon,
    rail: "bg-danger",
    glyph: "bg-danger-soft text-danger",
    label: "Error",
  },
  success: {
    icon: CheckIcon,
    rail: "bg-success",
    glyph: "bg-success-soft text-success",
    label: "Success",
  },
  info: { icon: AlertIcon, rail: "bg-info", glyph: "bg-info-soft text-info", label: "Notice" },
};

/**
 * Where toasts appear. Mounted once, at the root.
 *
 * Bottom-right, stacked newest-last, above everything. Bottom rather than top
 * because the top of this panel is a fixed bar with the search and the account
 * menu in it, and a notification that covers the thing you were about to click
 * is a notification you learn to resent.
 *
 * Two live regions, not one. An error is `assertive` — it interrupts, because
 * it is reporting that something the person asked for did not happen. Anything
 * else is `polite` and waits for a gap in whatever the screen reader is
 * already saying. Putting both in one region would make every "Saved" barge
 * into the middle of a sentence.
 *
 * The region elements are always mounted, empty. A live region inserted into
 * the document at the same moment its text appears is frequently missed
 * entirely — the browser has to be watching the node *before* it changes.
 *
 * Portalled into the topmost open modal `<dialog>` while there is one. A modal
 * dialog sits in the browser's top layer, which no `z-index` can climb over,
 * and everything outside it is inert — so a toast left in `body` paints
 * behind the dialog and cannot be clicked. `Select` does the same with its
 * menu.
 */
export function Toaster() {
  const toasts = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  const host = useTopDialog();

  const errors = toasts.filter((item) => item.tone === "error");
  const rest = toasts.filter((item) => item.tone !== "error");

  return createPortal(
    <div
      // `pointer-events-none` on the container and back on for each toast, so
      // the empty space beside them does not swallow clicks on the page below.
      className="pointer-events-none fixed inset-x-0 bottom-0 z-[100] flex flex-col items-end gap-2 p-4 sm:p-6"
    >
      <div role="alert" aria-live="assertive" className="contents">
        {errors.map((item) => (
          <ToastCard key={item.id} toast={item} />
        ))}
      </div>
      <div role="status" aria-live="polite" className="contents">
        {rest.map((item) => (
          <ToastCard key={item.id} toast={item} />
        ))}
      </div>
    </div>,
    host ?? document.body,
  );
}

/**
 * The modal dialog opened most recently and still open, or null.
 *
 * Watched rather than asked for: the dialogs belong to other components, and
 * `open` flipping on any `<dialog>` in the document is the one signal they all
 * share. Kept as a stack in open order, since the DOM order of nested dialogs
 * (an image preview inside the announcement editor) says nothing about which
 * is on top.
 */
function useTopDialog(): HTMLDialogElement | null {
  const [top, setTop] = useState<HTMLDialogElement | null>(null);

  useEffect(() => {
    const stack: HTMLDialogElement[] = [];
    const sync = () => {
      for (let i = stack.length - 1; i >= 0; i -= 1) {
        if (!stack[i]!.isConnected || !stack[i]!.open) stack.splice(i, 1);
      }
      for (const dialog of document.querySelectorAll("dialog")) {
        if (dialog.open && !stack.includes(dialog)) stack.push(dialog);
      }
      setTop(stack.at(-1) ?? null);
    };

    sync();
    const observer = new MutationObserver(sync);
    observer.observe(document.body, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ["open"],
    });
    return () => observer.disconnect();
  }, []);

  return top;
}

function ToastCard({ toast }: { toast: ToastRecord }) {
  const tone = TONE[toast.tone];
  const Glyph = tone.icon;

  return (
    <div
      className={cn(
        "panel rounded-panel pointer-events-auto relative w-full max-w-sm overflow-hidden",
        "flex items-start gap-3 py-3 pr-2 pl-4 shadow-lg",
        "animate-rise",
      )}
    >
      {/* A rail rather than a tinted card. The panel keeps the same surface as
          every other floating thing in the app, and the colour is one stripe
          instead of a wash that has to be contrast-checked in both themes. */}
      <span aria-hidden="true" className={cn("absolute inset-y-0 left-0 w-1", tone.rail)} />

      <span
        aria-hidden="true"
        className={cn(
          "rounded-control mt-0.5 flex size-6 shrink-0 items-center justify-center",
          tone.glyph,
        )}
      >
        <Glyph className="size-3.5" />
      </span>

      <div className="min-w-0 flex-1 py-0.5">
        <p className="text-content flex items-center gap-2 text-[0.8125rem] leading-snug font-medium">
          <span className="min-w-0 wrap-anywhere">{toast.title}</span>
          {toast.count > 1 ? (
            // The count is what stops "the API is down" reading as one
            // incident when it is really every query on the page.
            <span className="bg-surface-sunken text-content-muted shrink-0 rounded-full px-1.5 py-px text-[0.625rem] font-semibold tabular-nums">
              ×{toast.count}
            </span>
          ) : null}
        </p>

        {toast.description ? (
          <p className="text-content-muted mt-1 text-xs leading-relaxed wrap-anywhere">
            {toast.description}
          </p>
        ) : null}

        {toast.action ? (
          <button
            type="button"
            onClick={() => {
              toast.action?.onSelect();
              dismiss(toast.id);
            }}
            className="text-brand hover:text-brand-muted mt-2 text-xs font-semibold"
          >
            {toast.action.label}
          </button>
        ) : null}
      </div>

      <button
        type="button"
        onClick={() => dismiss(toast.id)}
        aria-label={`Dismiss ${tone.label.toLowerCase()}: ${toast.title}`}
        className={cn(
          "text-content-subtle hover:text-content hover:bg-surface-sunken",
          "rounded-control flex size-7 shrink-0 items-center justify-center transition-colors",
        )}
      >
        <CloseIcon className="size-3.5" />
      </button>
    </div>
  );
}
